"use client";

/**
 * "Size guide" button + modal for mystery-FRESH bouquets. The mystery range is
 * a size ladder (Simple → Luxury); this shows every tier small→large so a
 * customer can reference sizes, enlarge the illustrated chart, and jump
 * straight to a different size.
 *
 * Data split:
 *  - price / image / name / ORDER come LIVE from the products
 *    (`GET /api/products?q=mystery&category=Fresh&sort=priceAsc`), so they
 *    update whenever a product does — no hard-coded prices.
 *  - the editorial layer (on/off, intro, reference image, per-tier size label +
 *    "best for") is admin-configured in Settings. The PDP resolves it
 *    SERVER-SIDE and passes it in as `config`; only callers that have no server
 *    value (the client-side quick-view drawer) fall back to fetching
 *    `GET /api/products/settings`. Blanks fall back to built-in defaults.
 *
 * ⚠ `config` is plain serialisable data, never a module: `lib/products-settings`
 * value-imports `./r2-client` and react `cache`, so a client component cannot
 * value-import from it (the trap documented at CheckoutClient.tsx:87-93, which
 * once broke checkout). The TYPE comes from `lib/mystery-colour-schemes`, which
 * this file already imports and which is client-safe.
 *
 * Radix Dialogs throughout so it layers correctly even when opened from inside
 * the quick-view drawer, and the chart's enlarge view stacks on top of the
 * guide (Escape closes only the top layer). The tier ladder is still lazy —
 * fetched only when the modal opens.
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { Ruler, Check, Loader2, ZoomIn, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatPrice } from "@/lib/format-price";
import { productPath } from "@/lib/product-url";
import { cn } from "@/lib/utils";
import {
  defaultSizeMetaForTitle,
  isMysteryBouquetTitle,
  DEFAULT_MYSTERY_SIZE_GUIDE_IMAGE,
  type MysterySizeGuideConfig,
  type MysterySizeGuideEntry,
} from "@/lib/mystery-colour-schemes";

interface Tier {
  id: string;
  slug?: string | null;
  title: string;
  pricePence: number | null;
  imageUrl?: string;
  imageUrlRaw?: string;
  imageKey?: string;
}

const IMAGE_ALT =
  "Mystery bouquet size guide — a figure holding each size from XS (Simple) to XXL (Luxury)";

export function MysterySizeGuide({
  currentProductId,
  config,
  className,
}: {
  currentProductId: string;
  /**
   * Admin size-guide config, already resolved SERVER-SIDE by the PDP
   * (`app/(main)/p/[id]/page.tsx` reads the whole settings object anyway).
   * Passing it in removes a `/api/products/settings` round-trip on every
   * mystery-fresh PDP for data the server had already fetched.
   *
   * Omit / pass `undefined` from a caller that has no server value (the
   * client-side quick-view drawer) and the component fetches it on mount, as
   * before.
   */
  config?: MysterySizeGuideConfig | null;
  className?: string;
}) {
  const seeded = config != null;

  // Optimistic: assume enabled (the default) so the button shows instantly on
  // the common path; a seeded config is authoritative immediately, and the fetch
  // fallback flips it off only if an admin disabled it.
  const [enabled, setEnabled] = useState(config ? config.enabled !== false : true);
  const [intro, setIntro] = useState(
    typeof config?.intro === "string" ? config.intro : ""
  );
  const [imageUrl, setImageUrl] = useState<string | undefined>(() => {
    // Distinguish "field present" (respect it, incl. blank = hide) from absent.
    if (config && "imageUrl" in config) return config.imageUrl || undefined;
    return DEFAULT_MYSTERY_SIZE_GUIDE_IMAGE;
  });
  const [entries, setEntries] = useState<MysterySizeGuideEntry[]>(
    Array.isArray(config?.entries) ? config.entries : []
  );

  const [open, setOpen] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [tiers, setTiers] = useState<Tier[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Config on mount — respects the on/off switch + pulls intro/image/entries.
  // Skipped entirely when the server already resolved it (the PDP path).
  useEffect(() => {
    if (seeded) return;
    let cancelled = false;
    fetch("/api/products/settings")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const g = (
          data as {
            mysterySizeGuide?: {
              enabled?: boolean;
              intro?: string;
              imageUrl?: string;
              entries?: MysterySizeGuideEntry[];
            };
          } | null
        )?.mysterySizeGuide;
        if (cancelled || !g) return;
        setEnabled(g.enabled !== false);
        if (typeof g.intro === "string") setIntro(g.intro);
        // Distinguish "field present" (respect it, incl. blank = hide) from absent.
        if ("imageUrl" in g) setImageUrl(g.imageUrl || undefined);
        if (Array.isArray(g.entries)) setEntries(g.entries);
      })
      .catch(() => {
        /* keep optimistic defaults on a config-fetch hiccup */
      });
    return () => {
      cancelled = true;
    };
  }, [seeded]);

  // While the enlarge overlay is open, flag it on <body> so the guide Dialog's
  // dismiss guards can tell (the overlay portals outside the Dialog, so a click
  // on it would otherwise register as an "interact outside" and close the guide).
  useEffect(() => {
    if (!zoomOpen) return;
    document.body.setAttribute("data-sizeguide-zoom-open", "");
    return () => {
      document.body.removeAttribute("data-sizeguide-zoom-open");
    };
  }, [zoomOpen]);

  async function loadTiers() {
    if (tiers || loading) return;
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(
        "/api/products?q=mystery&category=Fresh&sort=priceAsc&limit=12"
      );
      if (!res.ok) throw new Error("bad status");
      const data = (await res.json()) as { items?: Tier[] };
      const items = (data.items ?? []).filter((p) => isMysteryBouquetTitle(p.title));
      setTiers(items);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  function handleOpen() {
    setOpen(true);
    void loadTiers();
  }

  if (!enabled) return null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleOpen}
        className={cn(
          "inline-flex min-h-11 items-center gap-1.5 sm:min-h-9",
          className
        )}
      >
        <Ruler className="h-4 w-4 text-primary" />
        Size guide
      </Button>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setZoomOpen(false);
        }}
      >
        <DialogContent
          // DIALOGVH: dvh, never vh. vh is the viewport measured with the mobile
          // URL bar retracted, so the guide overhung the visible area and its
          // bottom rows could not be reached.
          className="max-h-[92dvh] gap-3 sm:max-w-4xl"
          // The enlarge overlay portals outside this content — don't let a
          // click on it (or Escape while it's open) dismiss the guide.
          onPointerDownOutside={(e) => {
            if (document.body.hasAttribute("data-sizeguide-zoom-open")) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (document.body.hasAttribute("data-sizeguide-zoom-open")) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (zoomOpen) {
              e.preventDefault();
              setZoomOpen(false);
            }
          }}
        >
          <DialogHeader>
            {/* The illustrated chart carries its own visible title, so the
                Dialog's title stays for a11y only when the image is shown. */}
            <DialogTitle className={imageUrl ? "sr-only" : "font-serif"}>
              Mystery bouquet size guide
            </DialogTitle>
            <DialogDescription className="sr-only">
              Reference sizes for our mystery fresh bouquets, from smallest to largest.
            </DialogDescription>
          </DialogHeader>

          {/* The scroller INSIDE the 92dvh shell. It has to move to `dvh` too:
              the shell has no overflow of its own, so an 80vh child inside a
              92dvh parent overflows it by the URL-bar delta and clips. */}
          <div className="-mx-2 max-h-[80dvh] space-y-4 overflow-y-auto px-2">
            {imageUrl && (
              <button
                type="button"
                onClick={() => setZoomOpen(true)}
                aria-label="Enlarge the size guide chart"
                className="group relative block w-full overflow-hidden rounded-lg border border-border/50"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={imageUrl}
                  alt={IMAGE_ALT}
                  className="mx-auto max-h-[46vh] w-full cursor-zoom-in object-contain transition-transform duration-300 group-hover:scale-[1.01]"
                />
                <span className="pointer-events-none absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm">
                  <ZoomIn className="h-3.5 w-3.5" />
                  Enlarge
                </span>
              </button>
            )}
            {intro && <p className="text-sm text-muted-foreground">{intro}</p>}

            {loading && (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading sizes…
              </div>
            )}

            {!loading && error && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Couldn&apos;t load the sizes just now. See them on our{" "}
                <a href="/mystery-bouquets" className="font-medium text-primary underline underline-offset-2">
                  mystery bouquets page
                </a>
                .
              </p>
            )}

            {!loading && !error && tiers && tiers.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No sizes to show right now.
              </p>
            )}

            {!loading && !error && tiers && tiers.length > 0 && (
              <ul className="space-y-2">
                {tiers.map((t, i) => {
                  const entry = entries.find((e) => e.productId === t.id);
                  const fallback = defaultSizeMetaForTitle(t.title);
                  const sizeLabel = entry?.sizeLabel || fallback?.sizeLabel || null;
                  const bestFor = entry?.bestFor || fallback?.bestFor || null;
                  const isCurrent = t.id === currentProductId;
                  const img = t.imageUrlRaw || t.imageUrl;
                  // Relative scale bar — position in the price-ordered ladder.
                  const scalePct = Math.round(((i + 1) / tiers.length) * 100);
                  const name =
                    t.title.replace(/\s*mystery bouquets?\s*/i, "").trim() || t.title;

                  const inner = (
                    <>
                      <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-muted">
                        {img ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={img}
                            alt={t.title}
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        ) : null}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="truncate font-medium text-foreground" title={t.title}>
                            {name}
                          </span>
                          {sizeLabel && (
                            <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
                              {sizeLabel}
                            </span>
                          )}
                          {isCurrent && (
                            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold text-primary-foreground">
                              <Check className="h-3 w-3" />
                              You&apos;re viewing
                            </span>
                          )}
                        </div>
                        {bestFor && (
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">{bestFor}</p>
                        )}
                        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-primary/60"
                            style={{ width: `${scalePct}%` }}
                          />
                        </div>
                      </div>

                      <span className="flex shrink-0 items-center gap-1 self-start pt-0.5 text-sm font-semibold tabular-nums text-foreground">
                        {t.pricePence != null ? formatPrice(t.pricePence) : "—"}
                        {!isCurrent && (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                        )}
                      </span>
                    </>
                  );

                  const rowClass = cn(
                    "flex items-center gap-3 rounded-xl border p-2.5 sm:p-3",
                    isCurrent
                      ? "border-primary bg-primary/5 ring-1 ring-primary/40"
                      : "border-border bg-card transition-colors hover:border-primary/40 hover:bg-muted/40"
                  );

                  return (
                    <li key={t.id}>
                      {isCurrent ? (
                        <div className={rowClass}>{inner}</div>
                      ) : (
                        // Jump straight to a different size. Navigating closes
                        // the guide (and the quick-view, if open) via the route
                        // change.
                        <Link
                          href={productPath({ slug: t.slug, id: t.id })}
                          onClick={() => setOpen(false)}
                          aria-label={`View ${name}${sizeLabel ? ` (size ${sizeLabel})` : ""}`}
                          className={rowClass}
                        >
                          {inner}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            All tiers are the same designer&apos;s-choice mystery bouquet — only the size &amp; fullness change. You still pick the colour scheme on each product.
          </p>
        </DialogContent>
      </Dialog>

      {/* Enlarged chart — a full-screen portal overlay (NOT a Radix dialog,
          whose content can't exceed the viewport). On desktop it fits large and
          crisp; on mobile the chart is shown big and the overlay scrolls so the
          fine print stays legible. `pointer-events-auto` is load-bearing: the
          guide Dialog sets `body { pointer-events: none }`, which this portal
          would otherwise inherit. */}
      {zoomOpen &&
        imageUrl &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            data-sizeguide-zoom
            role="dialog"
            aria-modal="true"
            aria-label="Size guide — enlarged"
            className="pointer-events-auto fixed inset-0 z-9999 overflow-auto bg-black/85 backdrop-blur-sm"
            onClick={() => setZoomOpen(false)}
          >
            <button
              type="button"
              aria-label="Close enlarged view"
              onClick={(e) => {
                e.stopPropagation();
                setZoomOpen(false);
              }}
              className="fixed right-3 top-3 z-10 flex size-11 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/30 md:size-10 [&_svg]:size-5"
            >
              <X />
            </button>
            <div className="flex min-h-full min-w-full items-center justify-center p-4">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={imageUrl}
                alt={IMAGE_ALT}
                onClick={(e) => e.stopPropagation()}
                className="h-auto w-auto min-w-205 max-w-225 rounded-lg shadow-2xl sm:min-w-0 sm:max-h-[92vh] sm:max-w-[96vw]"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
